import { useState } from 'react';

import type { THoliday } from '@crehana/compass.dashlets';
import {
  ComplexDialog,
  ComplexDialogBanner,
  ComplexDialogBannerIcon,
  ComplexDialogBannerTitle,
  ComplexDialogBody,
  ComplexDialogContent,
  ComplexDialogFooter,
  ComplexDialogHeader,
  ComplexDialogPrimaryButton,
  ComplexDialogTitle,
} from '@crehana/compass.ui/modals/dialogs/complex-dialog';
import type {
  ColumnFiltersState,
  PaginationState,
} from '@crehana/compass.ui/table/tanstack';

import { useHomeTranslations } from '../../../../hooks/use-home-translations';
import { useDebounceValue } from './hooks/use-debounce-value';
import { HolidayDetailTable } from './modal-table';
import { useGetHolidayDetails } from './use-get-holiday-details';

interface ModalHolidayDetailsProps {
  holiday: THoliday | null;
  isOpen: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 3;
export const ModalHolidayDetails = ({
  holiday,
  isOpen,
  onClose,
}: ModalHolidayDetailsProps) => {
  const { t } = useHomeTranslations();
  const [search, setSearch] = useState<string>('');
  const debounceValue = useDebounceValue(search, 500);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0, // 0 based index
    pageSize: PAGE_SIZE,
  });

  const { data, loading, error, refetch } = useGetHolidayDetails({
    skip: !holiday?.id,
    variables: {
      // safely cast to string because it will not run if holidayId is undefined (check the skip param above)
      holidayId: holiday?.id as string,
      search: { query: debounceValue },
      filter: {
        jobPosition: columnFilters
          .filter(filter => filter.id === 'jobPosition')
          .map(filter => filter.value as string),
        country: columnFilters
          .filter(f => f.id === 'country')
          .map(f => f.value as string),
      },
      page: {
        number: pagination.pageIndex,
        limit: pagination.pageSize,
      },
    },
  });

  return (
    <ComplexDialog open={isOpen} onOpenChange={() => onClose()}>
      <ComplexDialogContent>
        <ComplexDialogHeader>
          <ComplexDialogTitle className="tw-flex tw-flex-col tw-gap-0.2 tw-text-left tw-items-start">
            {t('HOLIDAY_DETAIL_MODAL_TITLE')}
            <span className="tw-font-body5 tw-font-normal tw-text-neutral-light-700">
              {t('HOLIDAY_DETAIL_MODAL_TITLE_DESCRIPTION')}
            </span>
          </ComplexDialogTitle>
        </ComplexDialogHeader>

        <ComplexDialogBanner>
          <ComplexDialogBannerIcon />
          <ComplexDialogBannerTitle>
            {t('HOLIDAY_DETAIL_MODAL_INFO')}
          </ComplexDialogBannerTitle>
        </ComplexDialogBanner>

        <ComplexDialogBody>
          <HolidayDetailTable
            loading={loading}
            data={data}
            error={error}
            pagination={pagination}
            onPagination={setPagination}
            search={debounceValue}
            onSearchChange={setSearch}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            refetch={refetch}
          />
        </ComplexDialogBody>
        <ComplexDialogFooter className="tw-flex tw-justify-end tw-text-left">
          <ComplexDialogPrimaryButton type="button" onClick={onClose}>
            {t('GO_BACK')}
          </ComplexDialogPrimaryButton>
        </ComplexDialogFooter>
      </ComplexDialogContent>
    </ComplexDialog>
  );
};
